<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';
    import { getPredictiveState, setPredictiveChoice, decayName, type PredictiveState } from '@/PredictiveMode/predictive';
    import route from '../Util/routes';
    import FitnessSignature from './FitnessSignature.svelte';
    import PowerCurveV2 from './PowerCurveV2.svelte';
    import ToggleButtonSmall from '../Bootstrap/ToggleButtonSmall.svelte'
    import { createSettingsSubmitHandler } from '@/Util/utils';
    import ActivityDetailsTable from './ActivityDetailsTable.svelte';
    import { Button } from '@/lib/components/ui/button';
    import * as Popover from '$lib/components/ui/popover';
    import PredictiveModeSummary from '../PredictiveMode/PredictiveModeSummary.svelte';

    export let profileParams;
    export let ir_params;
    export let data;
    export let manualFitnessSignatures;

    // Whether anything has been edited since it was shown or last saved, for the page's Save All
    export let changed = false;
    let tableChanged = false;
    $: changed = decayChanged || tableChanged;

    // Save All sends what has been edited: the decay method if it was changed, and the activities opened in the table
    export function submitHandler(){
        fitnessSignatureTable?.saveAll();
        if (decayChanged) decayMethodHandler()
    }

    // Predictive Mode (Goal and Race plans) manages the decay method while it is on: the setting reads "XFAI Managed"
    // and is not posted, since choosing a decay method by hand ends the mode. That includes the time between the athlete's
    // choice and the plan's first activity, when the mode is on but not yet running: it takes the setting over then.
    let predictive: PredictiveState | null = null;
    $: managed = !!predictive?.enabled || (!!predictive?.applies && !predictive.ended && predictive.choice === 'on');
    onMount(async () => { predictive = await getPredictiveState(); });
    // The summary opens to the right of its link; with no room beside it on a phone it opens below instead.
    let viewportWidth = 1024;
    $: summarySide = viewportWidth >= 768 ? 'right' : 'bottom';
    let switching = false;
    async function setMode(choice: 'on' | 'off') {
        switching = true;
        try {
            await setPredictiveChoice(choice);
            window.location.reload();   // the decay method (and the signature) change with it
        } finally {
            switching = false;
        }
    }
    
  ///signature decay starts
  enum SignatureDecay {
        None = 1,
        Small = 1.03,
        Optimal = 1.1,
        Aggressive = 1.2,
    }

    const SignatureDecayItems = [
        {value:SignatureDecay.None, label:"None - Training Load Matched" },
        {value:SignatureDecay.Small, label:"Small" },
        {value:SignatureDecay.Optimal, label:"Optimal - Default" },
        {value:SignatureDecay.Aggressive, label:"Aggressive" },
    ]
    // $: console.log("fs tab:",profileParams)
  $: setSignatureOptions (ir_params)
    export let signatureOptions = {recalc_from_bt:true, signature_decay_method:SignatureDecay.None};
    function setSignatureOptions(val) {
        if(!val) return
        signatureOptions.signature_decay_method =  SignatureDecay.Optimal;
        if(val?.decay_method)
            SignatureDecayItems.forEach(({value, label}, index)=>{
                if(val?.decay_method == value) 
                    signatureOptions.signature_decay_method = value;
            })

        savedDecay = signatureOptions.signature_decay_method
    }

    // The decay method the server has, to know when it has been edited
    let savedDecay = null
    $: decayChanged = !managed && savedDecay !== null && signatureOptions.signature_decay_method !== savedDecay

    const postDecayMethod = createSettingsSubmitHandler(route('UserController@postSignatureDecayMethod'), () => signatureOptions);
    const decayMethodHandler = async () => {
        if (managed) return
        const sent = signatureOptions.signature_decay_method   // a choice made while it is on its way stays to be saved
        if (await postDecayMethod() !== false) savedDecay = sent
    };

    const onSave = () => {
        signatureOptions.recalc_from_bt = false
        decayMethodHandler()
    }
    const onRecalculate = () => {
        signatureOptions.recalc_from_bt = true
        decayMethodHandler()
    }

    let fitnessSignatureTable = null;
    let powerCurve: { reloadCurrentPowerCurve: () => void };
    let currentSignature;
    let decayModified = false

    $: currentSignature, powerCurve?.reloadCurrentPowerCurve();


    //signature decay ends
</script>

<svelte:window bind:innerWidth={viewportWidth} />

<div id="tab-grid-container">
    <div id="decay-method-portlet" class="portlet light bordered p-4 border-2">
        <div class="subtitle m-2 mb-7">
            <h4>{"Signature Decay"}</h4>
        </div>
        <div class="flex flex-col grow gap-2 m-2">
            <div class="flex flex-row gap-2 items-center">
                <span>
                    Decay Method:
                    <a href="https://www.baronbiosys.com/glossary/signature-decay-method" target="_help">
                        <div class="tooltip-icon">
                            <i class="fa-regular fa-circle-question" aria-hidden="true"></i>
                        </div>
                    </a>
                </span>
                <div>
                    {#if managed}
                        <select class="rounded py-[5px] px-[10px]" disabled aria-describedby="predictive-mode-note"><option>XFAI Managed Predictive Mode</option></select>
                    {:else}
                        <select class="rounded py-[5px] px-[10px]" bind:value={signatureOptions.signature_decay_method} on:input={() => decayModified=true} >
                            {#each SignatureDecayItems as {label, value}}
                                    <option value={value}>{label}</option>
                            {/each}
                        </select>
                    {/if}
                </div>
            </div>
            {#if predictive?.applies}
                <div id="predictive-mode-note" class="text-sm mt-2">
                    {#if managed}
                        <p>Your signature follows your plan's training loads, with no decay, until the plan ends. The decay method will return to <b>{decayName(predictive.previous_decay_method)}</b> then.</p>
                    {:else if predictive.ended}
                        <p>{predictive.choice === 'on' ? $_('report.predictive.ended_on') : $_('report.predictive.ended_off')}</p>
                    {:else if predictive.choice === 'on'}
                        <p>Predictive Mode will take over this setting with your first activity in your plan.</p>
                    {:else if predictive.choice === 'off'}
                        <p>Predictive Mode is off: your decay method applies during your plan.</p>
                    {/if}
                    <p class="mt-1">
                        {#if predictive.choice === 'off'}
                            <button class="btn btn-default btn-sm" disabled={switching} on:click={() => setMode('on')}>{predictive.ended ? $_('report.predictive.turn_on_next') : 'Turn Predictive Mode on'}</button>
                        {:else}
                            <button class="btn btn-default btn-sm" disabled={switching} on:click={() => setMode('off')}>Turn Predictive Mode off</button>
                        {/if}
                        <Popover.Root portal="#shadcn-ui-dialog-target">
                            <Popover.Trigger class="ml-3 text-sm underline rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring text-[#2f7076] hover:text-[#1f4f54]">What is Predictive Mode?</Popover.Trigger>
                            <Popover.Content side={summarySide} align={summarySide === 'right' ? 'start' : 'center'} collisionPadding={12} class="w-[min(26rem,calc(100vw-1.5rem))] min-w-0 text-gray-600 text-sm space-y-2 max-h-[70vh] overflow-y-auto">
                                <PredictiveModeSummary {predictive} />
                            </Popover.Content>
                        </Popover.Root>
                    </p>
                </div>
            {/if}
        </div>
        <div class="flex grow flex-row gap-2 mt-7 justify-end">
            <Button on:click={onRecalculate} disabled={managed} title={managed ? $_('report.predictive.managed_unavailable') : undefined}>Recalculate from Last Breakthrough</Button>
            <Button on:click={onSave} disabled={managed || !decayChanged} title={managed ? $_('report.predictive.managed_unavailable') : undefined}>Save</Button>
        </div>
    </div>
    <div id="power-curve-portlet" class="portlet light bordered full-height-content full-height-content-scrollable p-4 border-2 curve-portlet">
        <div class="subtitle m-2">
            <h4>{"Current Power Curve"}</h4>
        </div>
        <div class="twp">
            <PowerCurveV2 bind:this={powerCurve} {data} enableHistorical={true} fullLegend={false}></PowerCurveV2>
        </div>
    </div>
    <div id="cfs-portlet" class="portlet light bordered full-height-content full-height-content-scrollable p-4 border-2 curve-portlet">
        <FitnessSignature bind:signature={currentSignature} title={"Current Fitness Signature"} showLTP={true} readonly={true} {profileParams}></FitnessSignature>
    </div>


</div>

<div id="fs-tab-table" class="portlet light bordered full-height-content full-height-content-scrollable p-4 border-2">
        
    <!-- TODO: fitness signature methods could be moved to the FitnessSignature component and exported here via FitnessSignature.prototype.methodName-->
    <ActivityDetailsTable bind:this={fitnessSignatureTable} bind:changed={tableChanged} component={FitnessSignature} table={manualFitnessSignatures}  endpoint={route('UserController@postFitnessSignature')} newEntry={async(activity) => {
        let fs = { pp: 0, hie: 0, ftp: 0, ltp: 0 };
        await window.fetch('/getFitnessSignature?path='+activity?.path).then(res=>res.json()).then(data=>{if(data){ fs = data }})
        return {path:activity?.path, date:activity?.start_date, name:activity?.name, fs, auto_calc:false, state:"new"}
    }}
    getBody={(entry)=>{let body = entry?.input?.getBody(); body.path=entry.path; return body;}}
    >
    <h4 slot="title">
        <span>Activities with Locked Signatures</span>
        <a href="https://baronbiosys.com/glossary/manual_signatures" target="_help">
            <div class="tooltip-icon">
                <i class="fa-regular fa-circle-question" aria-hidden="true"></i>
            </div>
        </a>
        
    </h4>
</ActivityDetailsTable>
</div>

<style  lang="scss">
 #tab-grid-container{
    display:grid;

    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(4, auto);
    column-gap: 20px;
    row-gap: 12px;
 }
@media only screen and (min-width: 1250px){

    #tab-grid-container{
        #decay-method-portlet{
            grid-column: 1 / span 2;
            grid-row: 2 / span 1;
            align-self:end;

        }

        #power-curve-portlet{
            grid-column: 3 / span 4;
            grid-row: span 4;
        }

        #cfs-portlet{
            grid-column: 1 / span 2;
            grid-row: 1 / span 1;
            
        }
    }

}
@media only screen and (min-width: 700px) and (max-width: 1250px) {

    #tab-grid-container{
    #decay-method-portlet{
        grid-column: 4 / span 3;
        grid-row: 1 / span 1;
        align-self:start;

    }

    #power-curve-portlet{
        grid-column: 1 / span 6;
        grid-row: 2 / span 3;
    }

    #cfs-portlet{
        grid-column: 1 / span 3;
        grid-row: 1 / span 1;
    }
}

}

@media only screen and (max-width: 700px) {

    #tab-grid-container{
    #decay-method-portlet{
        grid-column: 1 / span ;
        grid-row: 2 / span 1;
        align-self:end;

    }

    #power-curve-portlet{
        grid-column: 1 / span ;
        grid-row: span 4;

    }

    #cfs-portlet{
        grid-column: 1 / span ;
        grid-row: 1 / span 1;

    }
}
}



@media only screen and (max-width: 873px) {
        .curve-portlet {
            overflow-x: scroll;
        }
    }

</style>
